import { Command, Option } from "clipanion";
import * as fs from "node:fs";
import * as path from "node:path";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson } from "../../lib/output.js";
import { derivePin, PIN_REL_PATH } from "../../lib/agent-content-sync.js";
import { parseReleasePin } from "../../lib/release-pin.js";
import { tryResolveRepoRoot } from "../../lib/brain-paths.js";

export class AgentContentPinCommand extends M8tCommand {
  static paths = [["agent-content", "pin"]];
  static usage = Command.Usage({
    description: "Re-derive a pinned agent's ref + contentSha from its branch.",
    details:
      "Resolves <key>'s repo branch to an immutable commit, fetches THAT commit, and digests what it actually contains — so the recorded contentSha describes bytes that were read, not a branch that may have moved. Prints the new entry; --write applies it to installer/release-pin.json. Bumping a pin is a release act: it changes what a from-zero install deploys.",
    examples: [
      ["Show what ezra's pin would become", "$0 agent-content pin ezra"],
      ["Apply it", "$0 agent-content pin ezra --write"],
      ["Pin a non-default branch", "$0 agent-content pin ezra --branch curated"],
    ],
  });

  key = Option.String();
  branch = Option.String("--branch", { description: "Branch to resolve. Default: main." });
  write = Option.Boolean("--write", false, { description: "Apply the new entry to installer/release-pin.json." });
  root = Option.String("--root");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const key = typeof this.key === "string" ? this.key : undefined;
    if (!key) throw new LocalCliError({ code: "USAGE", message: "<key> is required (an agents.<key> from the release pin)" });
    const branch = (typeof this.branch === "string" ? this.branch : undefined) ?? "main";
    const root = path.resolve(
      (typeof this.root === "string" ? this.root : undefined) ?? tryResolveRepoRoot() ?? process.cwd(),
    );
    const pinPath = path.join(root, PIN_REL_PATH);

    const raw = fs.readFileSync(pinPath, "utf8");
    const pin = parseReleasePin(raw, pinPath);
    // Object.hasOwn rather than a truthiness check: indexing a Record is typed as
    // always-present (noUncheckedIndexedAccess is off), so `if (!current)` reads as
    // dead code to the linter even though an unknown key is the whole point of this
    // guard. Narrowing the check is the fix; deleting it would be the bug.
    if (!Object.hasOwn(pin.agents, key)) {
      throw new LocalCliError({
        code: "AGENT_PIN_UNKNOWN",
        message: `No agents.${key} in ${PIN_REL_PATH}.`,
        hint: `Known keys: ${Object.keys(pin.agents).join(", ") || "(none)"}.`,
      });
    }
    const current = pin.agents[key];

    const next = await derivePin({
      current,
      branch,
      deps: { env: this.context.env, onNotice: (m) => this.context.stderr.write(`  ${colors.dim(m)}\n`) },
    });

    if (this.output === "json") {
      this.context.stdout.write(renderJson({ key, branch, from: current, to: next }) + "\n");
    } else if (next.ref === current.ref && next.contentSha === current.contentSha && next.persona === current.persona) {
      this.context.stdout.write(`${colors.success("✓")} ${key} is already pinned to ${branch} (${next.ref.slice(0, 8)}).\n`);
    } else {
      this.context.stdout.write(`${colors.field(key)} → ${branch}\n`);
      const line = (label: string, a: string, b: string): void => {
        this.context.stdout.write(
          a === b ? `  ${colors.dim(`${label}: ${a} (unchanged)`)}\n` : `  ${label}: ${colors.dim(a)} → ${colors.field(b)}\n`,
        );
      };
      line("ref       ", current.ref, next.ref);
      line("contentSha", current.contentSha, next.contentSha);
      line("persona   ", current.persona, next.persona);
    }

    if (this.write === true) {
      // Targeted round-trip: re-serialize the whole document at the file's own
      // 2-space indent. The pin is machine-owned and already in that shape, so a
      // no-op derive rewrites it byte-identically — asserted by a test, because a
      // formatting-churning writer turns every pin bump into an unreviewable diff.
      const doc = JSON.parse(raw) as { agents: Record<string, unknown> };
      doc.agents[key] = { repo: next.repo, ref: next.ref, visibility: next.visibility, persona: next.persona, contentSha: next.contentSha };
      fs.writeFileSync(pinPath, JSON.stringify(doc, null, 2) + "\n");
      this.context.stdout.write(`${colors.success("✓")} wrote ${PIN_REL_PATH}\n`);
      this.context.stdout.write(`  ${colors.hint("note:")} bumping a pin is a release act — it changes what a from-zero install deploys.\n`);
    }
    return 0;
  }
}
